/**
 * Stat Normalization Service
 * Normalizes hero stats for balanced gameplay in mixed-level battles
 */
export class StatNormalizationService {
  /**
   * Normalize a single attribute value
   * @param {number} rawValue - Original attribute value
   * @param {Object} config - Normalization configuration
   * @param {string} attributeName - Name of the attribute (for per-attribute configs)
   * @returns {number} Normalized attribute value
   */
  static normalizeAttribute(rawValue, config = {}, attributeName = null) {
    if (!config || !config.enabled) {
      return rawValue; // No normalization
    }

    const strategy = config.strategy || 'hardCap';
    const baseValue = 10; // Default starting value

    switch (strategy) {
      case 'hardCap':
        // Simple hard cap: any value above cap becomes cap
        const cap = config.cap || 50;
        return Math.min(rawValue, cap);

      case 'softCap':
        // Soft cap: values above threshold have diminishing returns
        const threshold = config.threshold || 40;
        const maxValue = config.maxValue || 60;
        if (rawValue <= threshold) {
          return rawValue;
        }
        // Diminishing returns: each point above threshold gives less benefit
        const excess = rawValue - threshold;
        const scalingFactor = config.scalingFactor || 0.5; // 50% effectiveness above threshold
        const softCapped = threshold + (excess * scalingFactor);
        return Math.min(softCapped, maxValue);

      case 'levelBased':
        // Scale based on average level of battle participants
        const averageLevel = config.averageLevel || 15;
        const playerLevel = config.playerLevel || 1;
        const levelDifference = playerLevel - averageLevel;
        
        // If player is above average, reduce their stats
        if (levelDifference > 0) {
          const reductionFactor = 1 - (levelDifference * (config.reductionPerLevel || 0.02)); // 2% per level above average
          return baseValue + (rawValue - baseValue) * Math.max(0.5, reductionFactor); // Min 50% of bonus
        }
        // If player is below average, they keep full stats (or get a boost)
        return rawValue;

      case 'percentile':
        // Cap based on percentile of all players
        // Requires all player stats to be calculated first
        const percentile = config.percentile || 75; // Cap at 75th percentile
        const percentileValue = config.percentileValue || 50;
        if (rawValue > percentileValue) {
          return percentileValue;
        }
        return rawValue;

      case 'logarithmic':
        // Logarithmic scaling: diminishing returns built into formula
        const logBase = config.logBase || 2;
        const maxEffective = config.maxEffective || 50;
        if (rawValue <= baseValue) return rawValue;
        
        const bonus = rawValue - baseValue;
        // Logarithmic scaling: log2(bonus + 1) * scalingFactor
        const logBonus = Math.log2(bonus + 1) * (maxEffective / Math.log2(90)); // Scale to maxEffective
        return baseValue + Math.min(logBonus, maxEffective - baseValue);

      default:
        return rawValue;
    }
  }

  /**
   * Normalize all hero attributes
   * @param {Object} hero - Hero object with attributes
   * @param {Object} config - Battle normalization config
   * @param {Object} context - Additional context (averageLevel, allPlayers, etc.)
   * @returns {Object} Normalized attributes
   */
  static normalizeHeroStats(hero, config = {}, context = {}) {
    if (!config || !config.enabled) {
      return {
        strength: hero.strength || 10,
        endurance: hero.endurance || 10,
        coordination: hero.coordination || 10,
        quickness: hero.quickness || 10,
        focus: hero.focus || 10,
        self: hero.self || 10,
      };
    }

    const normalizedConfig = {
      ...config,
      playerLevel: hero.level || 1,
      averageLevel: context.averageLevel || hero.level || 1,
    };

    // Allow per-attribute overrides
    const getAttributeConfig = (attrName) => {
      return config.attributes?.[attrName] || config;
    };

    return {
      strength: this.normalizeAttribute(
        hero.strength || 10,
        getAttributeConfig('strength'),
        'strength'
      ),
      endurance: this.normalizeAttribute(
        hero.endurance || 10,
        getAttributeConfig('endurance'),
        'endurance'
      ),
      coordination: this.normalizeAttribute(
        hero.coordination || 10,
        getAttributeConfig('coordination'),
        'coordination'
      ),
      quickness: this.normalizeAttribute(
        hero.quickness || 10,
        getAttributeConfig('quickness'),
        'quickness'
      ),
      focus: this.normalizeAttribute(
        hero.focus || 10,
        getAttributeConfig('focus'),
        'focus'
      ),
      self: this.normalizeAttribute(
        hero.self || 10,
        getAttributeConfig('self'),
        'self'
      ),
    };
  }

  /**
   * Calculate average level from array of heroes/players
   * @param {Array} players - Array of player/hero objects with level property
   * @returns {number} Average level
   */
  static calculateAverageLevel(players) {
    if (!Array.isArray(players) || players.length === 0) return 1;
    const sum = players.reduce((acc, p) => acc + (p.level || 1), 0);
    return Math.round(sum / players.length);
  }
}

